import PropTypes from 'prop-types';
import { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '../../../actions/auth';
// @mui
import { styled, alpha } from '@mui/material/styles';
import { Box, Link, Button, Drawer, Typography, Avatar, Stack, IconButton, Tooltip } from '@mui/material';
// hooks
import useResponsive from '../../../hooks/useResponsive';
// components
import Iconify from '../../../components/iconify';
import Scrollbar from '../../../components/scrollbar';
import NavSection from '../../../components/nav-section';
//
import { getNavConfig } from './config';

// ----------------------------------------------------------------------

const NAV_WIDTH = 280;

const StyledAccount = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2, 2.5),
  borderRadius: Number(theme.shape.borderRadius) * 1.5,
  backgroundColor: alpha(theme.palette.grey[500], 0.12),
}));

// ----------------------------------------------------------------------

Nav.propTypes = {
  openNav: PropTypes.bool,
  onCloseNav: PropTypes.func,
  isCollapsed: PropTypes.bool,
  onToggleCollapse: PropTypes.func,
};

export default function Nav({ openNav, onCloseNav, isCollapsed = false, onToggleCollapse }) {
  const { user: currentUser } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const isDesktop = useResponsive('up', 'lg');

  const userFirstName = useMemo(() => {
    if (currentUser?.firstName) return currentUser.firstName;
    try {
      const stored = localStorage.getItem('user') || localStorage.getItem('profile');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.firstName) return parsed.firstName;
      }
    } catch {}
    if (currentUser?.name) {
      const first = String(currentUser.name).trim().split(' ')[0];
      if (first) return first;
    }
    return 'Member';
  }, [currentUser]);

  const userRoleDisplay = useMemo(() => {
    const simRole = sessionStorage.getItem('mtc_view_as_role');
    if (simRole) {
      if (simRole === 'financial' || simRole === 'financial_1') return 'Financial I';
      if (simRole === 'financial_2') return 'Financial II';
      if (simRole === 'financial_3') return 'Financial III';
      return simRole.charAt(0).toUpperCase() + simRole.slice(1);
    }
    const roles = currentUser?.roles || [];
    const rNames = roles.map((r) => ((typeof r === 'object' && r !== null ? r.name : String(r)) || '').toLowerCase().replace(/^role_/, ''));
    if (rNames.includes('president') || currentUser?.isPresident) return 'President';
    if (rNames.includes('admin') || currentUser?.isAdmin) return 'Admin';
    if (rNames.includes('financial') || rNames.includes('financial_1')) return 'Financial I';
    if (rNames.includes('financial_2')) return 'Financial II';
    if (rNames.includes('financial_3')) return 'Financial III';
    if (rNames.includes('registrar') || rNames.includes('secretary')) return 'Registrar';
    if (rNames.includes('business')) return 'Business';
    return 'Member';
  }, [currentUser]);

  const [navData, setNavData] = useState(() => getNavConfig(currentUser));

  useEffect(() => {
    setNavData(getNavConfig(currentUser));
  }, [pathname, currentUser]);

  useEffect(() => {
    const handleViewAsChange = () => {
      setNavData(getNavConfig(currentUser));
    };
    window.addEventListener('mtc-view-as-change', handleViewAsChange);
    return () => window.removeEventListener('mtc-view-as-change', handleViewAsChange);
  }, [currentUser]);

  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': { height: 1, display: 'flex', flexDirection: 'column' },
      }}
    >
      {/* Header bar with Brand and Collapse Button */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2.5,
          pt: 2.5,
          pb: 1,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            component="img"
            src="/assets/icons/mtc.png"
            alt="MTC Logo"
            sx={{ width: 36, height: 36, objectFit: 'contain' }}
          />
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0B3B60', lineHeight: 1.2 }}>
              MUTUAL TRUST
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
              Financial Services
            </Typography>
          </Box>
        </Stack>

        <Tooltip title={isDesktop ? 'Collapse sidebar (Ctrl+B)' : 'Close navigation'}>
          <IconButton
            size="small"
            onClick={isDesktop ? onToggleCollapse : onCloseNav}
            sx={{
              color: 'text.secondary',
              bgcolor: 'grey.100',
              border: '1px solid',
              borderColor: 'divider',
              '&:hover': { bgcolor: 'grey.200', color: '#0B3B60' },
            }}
          >
            <Iconify icon="eva:chevron-left-fill" width={20} height={20} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* User profile card */}
      <Box sx={{ mt: 2, mb: 3, mx: 2.5 }}>
        <Link underline="none">
          <StyledAccount>
            <Avatar
              sx={{
                bgcolor: '#0B3B60',
                color: '#ffffff',
                fontWeight: 700,
                width: 40,
                height: 40,
                fontSize: '1rem',
              }}
            >
              {(userFirstName || 'M').charAt(0).toUpperCase()}
            </Avatar>

            <Box sx={{ ml: 2, minWidth: 0, flex: 1 }}>
              <Typography variant="subtitle2" noWrap sx={{ color: 'text.primary', fontWeight: 700 }}>
                {userFirstName}
              </Typography>

              <Typography variant="body2" noWrap sx={{ color: 'text.secondary', textTransform: 'capitalize' }}>
                {userRoleDisplay}
              </Typography>
            </Box>
          </StyledAccount>
        </Link>
      </Box>

      <NavSection data={navData} />

      <Box sx={{ flexGrow: 1 }} />

      <Box sx={{ px: 2.5, pb: 3, mt: 4 }}>
        <Stack spacing={1.5}>
          {isDesktop && (
            <Button
              onClick={onToggleCollapse}
              variant="outlined"
              size="small"
              startIcon={<Iconify icon="eva:chevron-left-fill" />}
              sx={{
                color: 'text.secondary',
                borderColor: 'divider',
                textTransform: 'none',
                fontWeight: 600,
                py: 0.8,
                '&:hover': { borderColor: '#0B3B60', color: '#0B3B60', bgcolor: 'rgba(11, 59, 96, 0.04)' },
              }}
              fullWidth
            >
              Collapse Sidebar
            </Button>
          )}

          <Button
            onClick={() => { dispatch(logout()); navigate('/login'); }}
            variant="contained"
            color="error"
            startIcon={<Iconify icon="eva:log-out-fill" />}
            fullWidth
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Logout
          </Button>
        </Stack>
      </Box>
    </Scrollbar>
  );

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: isCollapsed ? 0 : NAV_WIDTH },
        transition: (theme) =>
          theme.transitions.create('width', {
            duration: theme.transitions.duration.shorter,
          }),
        overflow: 'hidden',
      }}
    >
      {isDesktop ? (
        <Drawer
          open={!isCollapsed}
          variant="persistent"
          PaperProps={{
            sx: {
              width: NAV_WIDTH,
              bgcolor: 'background.default',
              borderRightStyle: 'dashed',
              transition: (theme) =>
                theme.transitions.create(['transform', 'visibility'], {
                  duration: theme.transitions.duration.shorter,
                }),
              transform: isCollapsed ? `translateX(-${NAV_WIDTH}px)` : 'none',
              visibility: isCollapsed ? 'hidden' : 'visible',
            },
          }}
        >
          {renderContent}
        </Drawer>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          ModalProps={{
            keepMounted: true,
          }}
          PaperProps={{
            sx: { width: NAV_WIDTH },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}
